/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, observable } from 'mobx';
import { LabelDto } from 'src/clu/modules/hooks/useCluUtteranceActions';
import { OmitFunctions } from 'src/core/stores/types';
import { getHash } from 'src/core/utils/strings';
import { LabelRange } from 'src/luis/types/stores/utterances/EntityLabelStore';
import { DatasetType } from 'src/modules/modelTraining/types/trainingTypes';

type CluUtteranceStoreData = OmitFunctions<Omit<CluUtteranceStore, 'id'>>;
export type CluUtteranceEntityLabel = { entityName: string } & LabelRange;

export class CluUtteranceStore {
    public id: number;

    @observable public dataset: DatasetType;

    @observable public text: string;

    @observable public tokenizedText: string[];

    @observable public language: string;

    @observable public labeledIntentName: string;

    @observable public labeledEntities: CluUtteranceEntityLabel[];

    constructor(dto: Partial<CluUtteranceStoreData>) {
        this.text = dto.text;
        this.dataset = dto.dataset;
        this.id = getHash(dto.text);
        this.language = dto.language;
        this.tokenizedText = dto.tokenizedText;
        this.labeledIntentName = dto.labeledIntentName;
        this.labeledEntities = dto.labeledEntities ?? [];
    }

    @action
    public setLabeledEntities(entityLabels: CluUtteranceEntityLabel[]) {
        this.labeledEntities = entityLabels;
    }

    @action
    public addLabel(entityLabels: CluUtteranceEntityLabel) {
        this.labeledEntities = this.labeledEntities.concat(entityLabels);
    }

    @action
    public deleteLabels(entityLabels: LabelDto[]) {
        this.labeledEntities = this.labeledEntities.filter(
            label =>
                !entityLabels.find(
                    el =>
                        el.entityId === label.entityName &&
                        el.startTokenIndex === label.startCharIndex &&
                        el.endTokenIndex === label.endTokenIndex
                )
        );
    }

    @action
    public setLabeledIntent(intentName: string) {
        this.labeledIntentName = intentName;
    }

    @action
    public setLanguage(language: string) {
        this.language = language;
    }

    @action
    public setText(text: string) {
        this.text = text;
        this.tokenizedText = text.split('');
    }

    @action
    public setDataset(dataset: DatasetType) {
        this.dataset = dataset;
    }
}
